export type RowDirtyState = 'modified' | 'new' | 'removed' | null;

interface RowStateEntry {
  editing: boolean;
  dirty: RowDirtyState;
  scope: any;
}

// Tracks per-row "in modifica" / "modificata" / "nuova" / "eliminata" state for a single DxDataGrid,
// keyed by the grid's own keyExpr value (e.key from DevExtreme events). `editing` and
// `dirty` are independent on purpose: canceling an in-progress edit must fall back to
// whatever dirty state the row already had, not to "normal".
// `scope` is the key of the group a metric row belongs to: one metric grid shows every group in
// turn, and the group rows and the metric table header only report their own group's rows.
export class RowStateTracker {

  private readonly rows = new Map<any, RowStateEntry>();

  private entry(key: any, scope: any): RowStateEntry {
    let entry = this.rows.get(key);
    if (!entry) {
      entry = {editing: false, dirty: null, scope};
      this.rows.set(key, entry);
    }
    return entry;
  }

  private prune(key: any): void {
    const entry = this.rows.get(key);
    if (entry && !entry.editing && !entry.dirty) {
      this.rows.delete(key);
    }
  }

  setEditing(key: any, scope?: any): void {
    this.entry(key, scope).editing = true;
  }

  clearEditing(key: any): void {
    const entry = this.rows.get(key);
    if (!entry) return;
    entry.editing = false;
    this.prune(key);
  }

  markModified(key: any, scope?: any): void {
    const entry = this.entry(key, scope);
    if (entry.dirty !== 'new') {
      entry.dirty = 'modified';
    }
  }

  markNew(key: any, scope?: any): void {
    this.entry(key, scope).dirty = 'new';
  }

  // A row added and deleted before any save leaves nothing behind.
  markRemoved(key: any, scope?: any): void {
    if (this.rows.get(key)?.dirty === 'new') {
      this.rows.delete(key);
      return;
    }
    const entry = this.entry(key, scope);
    entry.editing = false;
    entry.dirty = 'removed';
  }

  reset(key: any): void {
    this.rows.delete(key);
  }

  resetAll(): void {
    this.rows.clear();
  }

  resetScope(scope: any): void {
    for (const [key, entry] of this.rows) {
      if (entry.scope === scope) this.rows.delete(key);
    }
  }

  clearAllEditing(): void {
    for (const key of [...this.rows.keys()]) this.clearEditing(key);
  }

  isEditing(key: any): boolean {
    return this.rows.get(key)?.editing ?? false;
  }

  dirtyOf(key: any): RowDirtyState {
    return this.rows.get(key)?.dirty ?? null;
  }

  hasEditing(): boolean {
    for (const entry of this.rows.values()) {
      if (entry.editing) return true;
    }
    return false;
  }

  hasAny(): boolean {
    return this.rows.size > 0;
  }

  hasAnyIn(scope: any): boolean {
    for (const entry of this.rows.values()) {
      if (entry.scope === scope) return true;
    }
    return false;
  }
}
